/**
 * idGraph.js — privacy/identity debugging over captured ad-traffic rows.
 *
 * Scans each row's URL query params and (defensively) JSON request bodies for
 * device/user identifiers, classifies them, tracks where they were seen, and
 * flags privacy issues: zeroed IDs, IDs leaking despite limit-ad-tracking, and
 * inconsistent values for the same id type across hosts.
 *
 * Never throws — malformed/garbage rows are skipped, not fatal.
 */

// ── ID param keys we scan in URL query strings (case-insensitive) ──────────
const ID_PARAM_KEYS = new Set([
  'rdid', 'idfa', 'ifa', 'gaid', 'adid', 'did', 'uid', 'uid2', 'muid',
]);

// ── ATT / limited-tracking signal keys ──────────────────────────────────────
const LAT_PARAM_KEYS = new Set(['is_lat', 'att', 'lmt']);

const MAX_BODY_BYTES = 256 * 1024; // 256KB cap on body parsing

const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

function isZeroed(value) {
  if (!value) return true;
  const v = String(value).toLowerCase();
  if (v === ZERO_UUID) return true;
  if (/^0+$/.test(v.replace(/-/g, ''))) return true;
  return false;
}

// Loose "looks like an ID" check: UUID-ish, 16+ hex chars, or 20+ base64-ish chars.
function looksLikeId(value) {
  if (value == null) return false;
  const v = String(value).trim();
  if (!v) return false;
  if (v === '0' || /^0+$/.test(v)) return false; // obvious non-ids
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (UUID_RE.test(v)) return true;
  const HEX_RE = /^[0-9a-f]{16,}$/i;
  if (HEX_RE.test(v)) return true;
  const B64_RE = /^[A-Za-z0-9+/_-]{20,}={0,2}$/;
  if (B64_RE.test(v)) return true;
  return false;
}

function hostOf(url) {
  try { return new URL(url).host; } catch { return 'unknown-host'; }
}

// idType classification for a param-key-based hit
function classifyParamKey(key) {
  const k = String(key).toLowerCase();
  if (k === 'idfa' || k === 'gaid' || k === 'rdid' || k === 'ifa' || k === 'adid' || k === 'did') {
    return 'idfa/gaid (device)';
  }
  if (k === 'uid2') return 'uid2';
  if (k === 'muid') return 'publisher user id';
  if (k === 'uid') return 'publisher user id';
  return 'unknown';
}

// idType classification for an eid source domain
function classifyEidSource(source) {
  const s = String(source || '').toLowerCase();
  if (s.includes('uidapi.com') || s.includes('uid2')) return 'uid2';
  if (s.includes('liveramp') || s.includes('rampid') || s.includes('idl_env')) return 'liveramp';
  if (!s) return 'unknown';
  return s; // per-eid source domain
}

function isDeviceIdType(idType) {
  return idType === 'idfa/gaid (device)';
}

function maskValue(value) {
  const v = String(value == null ? '' : value);
  if (v.length <= 12) return v; // too short to usefully mask
  return `${v.slice(0, 8)}…${v.slice(-4)}`;
}

// ── defensive helpers ────────────────────────────────────────────────────

function safeParseJson(str) {
  if (typeof str !== 'string') return null;
  if (str.length > MAX_BODY_BYTES) return null;
  const s = str.trim();
  if (!s || (s[0] !== '{' && s[0] !== '[')) return null;
  try { return JSON.parse(s); } catch { return null; }
}

function getBodyObject(row) {
  const rb = row && row.requestBody;
  if (rb && typeof rb === 'object') return rb;
  if (typeof rb === 'string') return safeParseJson(rb);
  return null;
}

function getAtPath(obj, path) {
  let cur = obj;
  for (const key of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
}

// ── core scan ────────────────────────────────────────────────────────────

/**
 * @param {Array<object>} rows
 * @returns {{ids: Array, attSignals: Array, summary: {total: number, leaks: number}}}
 */
export function buildIdGraph(rows) {
  const result = { ids: [], attSignals: [], summary: { total: 0, leaks: 0 } };
  if (!Array.isArray(rows) || rows.length === 0) return result;

  // key: `${idType}::${value}` -> record
  const idMap = new Map();
  const attSignals = [];
  let sessionHasLatSignal = false;

  const addHit = (idType, value, host, requestType) => {
    if (!looksLikeId(value)) return;
    const key = `${idType}::${value}`;
    let rec = idMap.get(key);
    if (!rec) {
      rec = {
        idType,
        value,
        maskedValue: maskValue(value),
        sources: new Map(), // `${host}::${requestType}` -> {host, requestType, count}
        flags: new Set(),
        zeroed: isZeroed(value),
      };
      idMap.set(key, rec);
    }
    const srcKey = `${host}::${requestType}`;
    const src = rec.sources.get(srcKey);
    if (src) src.count += 1;
    else rec.sources.set(srcKey, { host, requestType: requestType || 'unknown', count: 1 });
  };

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    let host = 'unknown-host';
    let requestType = row.requestType;

    // ── URL query params ──
    try {
      const u = new URL(row.url);
      host = u.host || host;
      for (const [rawKey, rawVal] of u.searchParams.entries()) {
        const key = String(rawKey).toLowerCase();
        if (LAT_PARAM_KEYS.has(key)) {
          const v = String(rawVal);
          attSignals.push({ source: `${host} (${key})`, value: v });
          if (v === '1' || v.toLowerCase() === 'true') sessionHasLatSignal = true;
          continue;
        }
        if (ID_PARAM_KEYS.has(key)) {
          addHit(classifyParamKey(key), rawVal, host, requestType);
        }
      }
    } catch {
      // bad/missing URL — still try to derive host from row if present
      if (row.url) host = hostOf(row.url);
    }

    // ── JSON body ──
    try {
      const body = getBodyObject(row);
      if (body && typeof body === 'object') {
        const ifa = getAtPath(body, ['device', 'ifa']);
        if (ifa != null) addHit('idfa/gaid (device)', ifa, host, requestType);

        const lmt = getAtPath(body, ['device', 'lmt']);
        if (lmt != null) {
          attSignals.push({ source: `${host} (device.lmt)`, value: String(lmt) });
          if (lmt === 1 || lmt === '1' || lmt === true) sessionHasLatSignal = true;
        }

        const userId = getAtPath(body, ['user', 'id']);
        if (userId != null) addHit('publisher user id', userId, host, requestType);

        const buyeruid = getAtPath(body, ['user', 'buyeruid']);
        if (buyeruid != null) addHit('publisher user id', buyeruid, host, requestType);

        const eids = getAtPath(body, ['user', 'ext', 'eids']);
        if (Array.isArray(eids)) {
          for (const eid of eids) {
            if (!eid || typeof eid !== 'object') continue;
            const source = eid.source;
            const idType = classifyEidSource(source);
            const uids = Array.isArray(eid.uids) ? eid.uids : [];
            for (const u of uids) {
              if (!u || typeof u !== 'object') continue;
              if (u.id != null) addHit(idType, u.id, host, requestType);
            }
          }
        }
      }
    } catch {
      // never throw on malformed bodies
    }
  }

  // ── post-process: flags ──
  // inconsistency: same idType, >1 distinct non-zero value
  const idTypeValues = new Map(); // idType -> Set(values)
  for (const rec of idMap.values()) {
    if (rec.zeroed) continue;
    if (!idTypeValues.has(rec.idType)) idTypeValues.set(rec.idType, new Set());
    idTypeValues.get(rec.idType).add(rec.value);
  }

  const ids = [];
  let leaks = 0;
  for (const rec of idMap.values()) {
    const flags = [];
    if (rec.zeroed) {
      flags.push('zeroed');
    } else {
      if (sessionHasLatSignal && isDeviceIdType(rec.idType)) {
        flags.push('leak: sent despite limit-tracking');
      }
      const distinctVals = idTypeValues.get(rec.idType);
      if (distinctVals && distinctVals.size > 1) {
        flags.push('inconsistent: multiple values for same idType across hosts');
      }
    }
    if (flags.some((f) => f.startsWith('leak'))) leaks += 1;

    ids.push({
      idType: rec.idType,
      value: rec.value,
      maskedValue: rec.maskedValue,
      sources: Array.from(rec.sources.values()),
      flags,
    });
  }

  result.ids = ids;
  result.attSignals = attSignals;
  result.summary = { total: ids.length, leaks };
  return result;
}
